import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { CodeSentDto, MeDto, SignedInDto } from '@tiffinify/contracts';
import { BtnPrimary, BtnSecondary, Card, ErrorText, Field, Helper, Logo, Mono, Skel, TiffinMini } from '../components/ui';
import { ApiClientError, get, post } from '../lib/api';
import { errorMessage } from '../lib/format';
import { useAuthConfig, useInvite } from '../lib/queries';

/*
 * Google sign-in with Google's standard redirect (OpenID Connect, an ID token in the URL fragment), so the button
 * can look like every other button here and it works in an installed app, where sign-in popups are unreliable.
 * The `state` proves the answer is for a sign-in this browser started; the `nonce` that the token answers it.
 */
const GOOGLE_KEY = 'tiffinify.google';
interface GoogleStart { state: string; nonce: string; invite: string | null }
type GoogleReturn = { credential: string; invite: string | null } | { error: string; invite: string | null };

const randomId = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');

function startGoogle(clientId: string, invite: string | null) {
  const start: GoogleStart = { state: randomId(), nonce: randomId(), invite };
  sessionStorage.setItem(GOOGLE_KEY, JSON.stringify(start));
  const q = new URLSearchParams({
    client_id: clientId,
    redirect_uri: `${location.origin}/welcome`,
    response_type: 'id_token',
    scope: 'openid email profile',
    prompt: 'select_account',
    state: start.state,
    nonce: start.nonce,
  });
  location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${q}`);
}

/** What Google sent back in the URL fragment, if this page load is the end of a Google sign-in. */
function googleReturn(): GoogleReturn | null {
  const hash = new URLSearchParams(location.hash.slice(1));
  if (!hash.has('state')) return null;
  let start: GoogleStart | null = null;
  try {
    start = JSON.parse(sessionStorage.getItem(GOOGLE_KEY) ?? 'null') as GoogleStart | null;
  } catch { /* nothing started here */ }
  const invite = start?.invite ?? null;
  if (!start || hash.get('state') !== start.state) return { error: 'That Google sign-in did not start here. Try again.', invite };
  const credential = hash.get('id_token');
  if (!credential) return { error: hash.get('error') === 'access_denied' ? 'Google sign-in was cancelled.' : 'Google sign-in did not finish. Try again.', invite };
  try {
    const claims = JSON.parse(atob(credential.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { nonce?: string };
    if (claims.nonce !== start.nonce) return { error: 'Google sign-in did not finish. Try again.', invite };
  } catch {
    return { error: 'Google sign-in did not finish. Try again.', invite };
  }
  return { credential, invite };
}

/** Google's "G", in Google's colours (their branding asks for it unchanged). */
function GoogleG() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** "Send again" waits this long, so a second tap does not use up another send. */
const RESEND_WAIT_MS = 20_000;

type Step = 'email' | 'code' | 'details';
/** What a first sign-in still needs (from `needs_details`). */
type Needs = { name?: string; kitchenName?: string };

export function Welcome() {
  const { token: linkToken } = useParams();
  // Back from Google: read once, then the fragment is cleared so a reload does not send it again.
  const [fromGoogle] = useState(googleReturn);
  // Google always comes back to /welcome; an invite started on /join/... carries on from here.
  const token = linkToken ?? fromGoogle?.invite ?? undefined;
  const nav = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kitchenName, setKitchenName] = useState('');
  const [sent, setSent] = useState<CodeSentDto | null>(null);
  const [needs, setNeeds] = useState<Needs>({});
  // A Google sign-in waiting for a name or kitchen name; sent again with them.
  const [credential, setCredential] = useState<string | null>(null);
  const [cooling, setCooling] = useState(false);
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState<false | 'code' | 'verify' | 'google' | 'demo'>(false);
  const [error, setError] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  // The invite this screen found used up (the same screen stays mounted when it moves to /welcome).
  const [goneFor, setGoneFor] = useState<string | null>(null);

  const config = useAuthConfig();
  const invite = useInvite(token);
  const health = useQuery({ queryKey: ['health'], queryFn: () => get<{ env: string }>('/health'), retry: false });
  const isDev = health.data?.env !== undefined && health.data.env !== 'production';
  const googleClientId = config.data?.googleClientId ?? null;
  const inviteGone = !!token && (invite.data?.valid === false || (invite.error instanceof ApiClientError && invite.error.status === 410) || goneFor === token);

  const coolTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(coolTimer.current), []);
  const startCooling = () => {
    setCooling(true);
    clearTimeout(coolTimer.current);
    coolTimer.current = setTimeout(() => setCooling(false), RESEND_WAIT_MS);
  };

  const clearErrors = () => { setError(''); setFields({}); };
  const fail = (e: unknown) => {
    if (e instanceof ApiClientError && e.status === 410 && token) return setGoneFor(token);
    const f = e instanceof ApiClientError ? e.fields : {};
    setFields(f);
    // A message that belongs to a field shows under it, not twice.
    setError(Object.keys(f).length > 0 ? '' : errorMessage(e));
  };
  /** True when the server asked for a name or kitchen name: those inputs appear and the same code or Google sign-in is sent again. */
  const askedForDetails = (e: unknown) => {
    if (!(e instanceof ApiClientError) || e.code !== 'needs_details') return false;
    setNeeds({ ...(e.fields.name ? { name: e.fields.name } : {}), ...(e.fields.kitchenName && !token ? { kitchenName: e.fields.kitchenName } : {}) });
    clearErrors();
    return true;
  };

  /** The first-time details asked for, checked here so a missing one does not cost a try. */
  function details(): { name?: string; kitchenName?: string; invite?: string } | null {
    const f: Record<string, string> = {};
    if (needs.name && !name.trim()) f.name = 'Enter your name';
    if (needs.kitchenName && kitchenName.trim().length < 2) f.kitchenName = 'Enter your kitchen name';
    if (Object.keys(f).length > 0) {
      setFields(f);
      return null;
    }
    return {
      ...(needs.name ? { name: name.trim() } : {}),
      ...(needs.kitchenName && !token ? { kitchenName: kitchenName.trim() } : {}),
      ...(token ? { invite: token } : {}),
    };
  }

  function signedIn({ createdKitchen, ...me }: SignedInDto) {
    qc.clear();
    qc.setQueryData<MeDto>(['me'], me);
    nav(createdKitchen ? '/settings?setup=1' : '/', { replace: true });
  }

  async function requestCode(again = false) {
    clearErrors();
    setBusy('code');
    try {
      const res = await post<CodeSentDto>('/auth/request-code', { email: email.trim() });
      setSent(res);
      setCode(res.devCode ?? '');
      setResent(again);
      setStep('code');
      startCooling();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    clearErrors();
    const d = details();
    if (!d) return;
    setBusy('verify');
    try {
      signedIn(await post<SignedInDto>('/auth/verify', { email: email.trim(), code, ...d }));
    } catch (e) {
      if (!askedForDetails(e)) fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function google(cred: string, withDetails: boolean) {
    clearErrors();
    const plain = token ? { invite: token } : {};
    const d = withDetails ? details() : plain;
    if (!d) return;
    setBusy('google');
    try {
      signedIn(await post<SignedInDto>('/auth/google', { credential: cred, ...d }));
    } catch (e) {
      if (askedForDetails(e)) {
        setCredential(cred);
        setStep('details');
      } else {
        fail(e);
      }
    } finally {
      setBusy(false);
    }
  }
  const finishedGoogle = useRef(false);
  useEffect(() => {
    if (!fromGoogle || finishedGoogle.current) return;
    finishedGoogle.current = true;
    sessionStorage.removeItem(GOOGLE_KEY);
    history.replaceState(null, '', location.pathname);
    if ('credential' in fromGoogle) void google(fromGoogle.credential, false);
    else setError(fromGoogle.error);
    // Runs once, for the page load that came back from Google.
  }, []);

  async function openDemo() {
    setBusy('demo');
    try {
      const me = await post<MeDto>('/dev/demo?reset=1');
      qc.clear();
      qc.setQueryData(['me'], me);
      nav('/', { replace: true });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  const [leaving, setLeaving] = useState(false);
  const showGoogle = !!googleClientId && step === 'email' && !inviteGone;
  const continueWithGoogle = () => {
    if (!googleClientId) return;
    setLeaving(true);
    startGoogle(googleClientId, token ?? null);
  };

  const changeEmail = () => {
    setStep('email'); setCode(''); setSent(null); setNeeds({}); setCredential(null); setResent(false); clearErrors();
  };

  const detailFields = (
    <>
      {needs.name !== undefined && (
        <Field label="Your name" id="su-name" value={name} onChange={setName} placeholder="Your full name" autoComplete="name" error={fields.name} />
      )}
      {needs.kitchenName !== undefined && (
        <Field label="Kitchen name" id="su-kitchen" value={kitchenName} onChange={setKitchenName} placeholder="Your kitchen name" autoComplete="organization" error={fields.kitchenName} />
      )}
    </>
  );
  const firstTime = needs.name !== undefined || needs.kitchenName !== undefined;
  const firstTimeNote = firstTime && <Helper icon="info">{token ? 'First time here? Tell us your name to join.' : 'First time here? A couple of details and your kitchen is ready.'}</Helper>;

  if (inviteGone) {
    return (
      <main className="main auth">
        <div className="between">
          <Logo size={20} />
          <Mono>Cloud kitchen software</Mono>
        </div>
        <div className="auth-block">
          <h1 className="auth-title">This invite link no longer works</h1>
          <p className="auth-lede">It has expired or was already used. Ask the kitchen owner for a new link.</p>
        </div>
        <BtnPrimary label="Go to sign in" to="/welcome" />
      </main>
    );
  }

  return (
    <main className="main auth">
      <div className="between">
        <Logo size={20} />
        <Mono>Cloud kitchen software</Mono>
      </div>

      {token ? (
        <div className="auth-block">
          <h1 className="auth-title">
            {invite.isPending ? <Skel w={240} /> : `Join ${invite.data?.kitchenName ?? 'your team'} on Tiffinify`}
          </h1>
          <p className="auth-lede">Sign in to see the day's orders and lists with the team.</p>
        </div>
      ) : (
        <div className="auth-block">
          <h1 className="auth-title">Plans, Menus and orders in one place</h1>
          <p className="auth-lede">Monthly plans, festival bookings and daily orders, with the kitchen list, deliveries and money worked out for you.</p>
        </div>
      )}

      {step === 'email' && (
        <form className="form-contents" noValidate onSubmit={(e) => { e.preventDefault(); if (!busy && email.trim()) void requestCode(); }}>
          {showGoogle && (
            <>
              <button type="button" className="btn-social" onClick={continueWithGoogle} disabled={!!busy || leaving} aria-busy={leaving || busy === 'google' || undefined}>
                {leaving || busy === 'google' ? <TiffinMini /> : <GoogleG />}
                {busy === 'google' ? 'Signing in…' : 'Continue with Google'}
              </button>
              <div className="or-line"><Mono muted>or use your email</Mono></div>
            </>
          )}
          <Card pad>
            <Field
              label="Your email" id="su-email" type="email" value={email} onChange={setEmail} autoComplete="email" placeholder="you@example.com"
              hint="We email you a 6-digit code. No password to remember." error={fields.email}
            />
          </Card>
          <ErrorText>{error}</ErrorText>
          <div className="auth-block">
            <BtnPrimary type="submit" busy={busy === 'code'} label={busy === 'code' ? 'Sending code…' : 'Email me a code'} disabled={!!busy || leaving || !email.trim()} />
            {!token && <span className="auth-note">New here or coming back, it's the same step. Free to start, no card needed.</span>}
          </div>
          {isDev && !token && (
            <div className="stack">
              <Helper icon="info">Test servers only: opens a demo kitchen with sample customers.</Helper>
              <BtnSecondary busy={busy === 'demo'} label={busy === 'demo' ? 'Opening the kitchen…' : 'Open the demo kitchen'} onClick={openDemo} disabled={!!busy} sm />
            </div>
          )}
        </form>
      )}

      {step === 'code' && (
        <form className="form-contents" noValidate onSubmit={(e) => { e.preventDefault(); if (!busy && code.length === 6) void verify(); }}>
          <Card pad>
            <Field
              label="6-digit code" id="su-code" value={code} onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000" maxLength={6}
              hint={`Sent to ${email.trim()}. Check spam if it is not there in a minute.`} error={fields.code}
            />
            {sent?.devCode && <Helper icon="info">Test code: {sent.devCode}</Helper>}
            {resent && !error && <Helper icon="check">A new code is on its way.</Helper>}
            {firstTimeNote}
            {detailFields}
          </Card>
          <ErrorText>{error}</ErrorText>
          <div className="auth-block">
            <BtnPrimary type="submit" busy={busy === 'verify'} label={busy === 'verify' ? 'Checking…' : 'Continue'} icon="check" disabled={!!busy || code.length !== 6} />
            <div className="between">
              <button type="button" className="text-link" onClick={changeEmail} disabled={!!busy}>Change email</button>
              <button type="button" className="text-link" onClick={() => void requestCode(true)} disabled={!!busy || cooling}>{busy === 'code' ? 'Sending…' : 'Send again'}</button>
            </div>
          </div>
        </form>
      )}

      {step === 'details' && credential && (
        <form className="form-contents" noValidate onSubmit={(e) => { e.preventDefault(); if (!busy) void google(credential, true); }}>
          <Card pad>
            {firstTimeNote}
            {detailFields}
          </Card>
          <ErrorText>{error}</ErrorText>
          <div className="auth-block">
            <BtnPrimary type="submit" busy={busy === 'google'} label={busy === 'google' ? 'Signing in…' : 'Continue'} icon="check" disabled={!!busy} />
            <button type="button" className="text-link" onClick={changeEmail} disabled={!!busy}>Back</button>
          </div>
        </form>
      )}
    </main>
  );
}
